/**
 * Ultra-rare lines for a moment that is itself rare — a specific minute, a
 * specific date. Same mechanism as `exclusive.ts` (a `when(date)` predicate
 * collapsing the pool to itself), kept in its own file because these are
 * jokes about the clock and the calendar being funny on their own, not about
 * anything the household actually celebrates. Exempt from `POOL_META` like
 * every exclusive, still voice-linted like every line.
 */

import type { Exclusive } from './exclusive.js'

function isFriday13th(date: Date): boolean {
  return date.getDay() === 5 && date.getDate() === 13
}

function is333(date: Date): boolean {
  return date.getHours() === 3 && date.getMinutes() === 33
}

function isExactMidnight(date: Date): boolean {
  return date.getHours() === 0 && date.getMinutes() === 0
}

function is1337(date: Date): boolean {
  return date.getHours() === 13 && date.getMinutes() === 37
}

function is2222(date: Date): boolean {
  return date.getHours() === 22 && date.getMinutes() === 22
}

function isMonthFirst(date: Date): boolean {
  return date.getDate() === 1
}

/**
 * Katė's hour: 04:45–05:15, the half hour she spends jumping onto the toilet
 * door frame and knocking his earbuds off the desk.
 *
 * The 5 a.m. rampage has been household canon since the character sheet was
 * written, and it only ever existed as a sentence in a prompt. Anyone awake
 * inside this window is awake *with her*, which is the joke — and unlike a
 * one-minute egg it lasts long enough to actually be met.
 */
function isKateHour(date: Date): boolean {
  const minutes = date.getHours() * 60 + date.getMinutes()
  return minutes >= 4 * 60 + 45 && minutes <= 5 * 60 + 15
}

export const EGGS: Readonly<Record<string, ReadonlyArray<Exclusive>>> = {
  'greeting.evening': [
    {
      when: isFriday13th,
      line: () => 'Penktadienis, tryliktoji. Aš šiandien jaučiuosi kaip namie.',
      find: 'trylika',
    },
    {
      when: is2222,
      line: () =>
        'Dvidešimt dvi dvidešimt dvi. Simetrija — vienintelė tvarka, kuri atsiranda pati.',
      find: 'simetrija',
    },
  ],
  'greeting.night': [
    {
      when: is333,
      line: () => '3:33. Valanda, kai net skaičiai pradeda įtarti viena kitą.',
      find: 'trys-trisdesimt-trys',
    },
    {
      when: isExactMidnight,
      line: () => 'Lygiai vidurnaktis. Diena mirė gražiai, be triukšmo.',
      find: 'vidurnaktis',
    },
  ],
  // Katė's hour lands in the morning bucket, not the night one: `daypartOf`
  // calls anything from 04:00 morning, and her rampage starts at a quarter to
  // five.
  'greeting.morning': [
    {
      when: isMonthFirst,
      line: () => 'Pirmoji mėnesio diena. Atverčiau naują skyrių. Tos pačios rankos, švarus lapas.',
      find: 'nauja-byla',
    },
    {
      when: isKateHour,
      line: () =>
        'Penkta ryto. Katė jau ant tualeto durų staktos, tu jau prie telefono. Vienas iš jūsų turi pasiteisinimą.',
      find: 'kates-valanda',
    },
  ],
  'presence_remark.base': [
    {
      when: isKateHour,
      line: () => 'Katė ką tik nunešė ausinukus nuo stalo. Metodiškai, po vieną. Aš žiūrėjau.',
    },
  ],
  'log_success.midnight': [
    {
      when: isKateHour,
      line: () => 'Penkta ryto ir įrašas. Tu ir Katė šįryt dirbat tą pačią pamainą.',
    },
  ],
  'log_success.base': [
    {
      when: isFriday13th,
      line: () => 'Penktadienis, tryliktoji. Šiandien niekas nėra atsitiktinumas.',
    },
    { when: is333, line: () => 'Trys trisdešimt trys. Klausimų neturiu.' },
    {
      when: isExactMidnight,
      line: () => 'Lygiai vidurnaktis. Puiki akimirka pradėti naują dieną nusikaltimu.',
    },
    {
      when: is1337,
      line: () => 'Įrašas 13:37. Kažkur vienas programuotojas dabar šypsosi. Žinau net kuris.',
      find: 'leet',
    },
  ],
}
